import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import whoIsItForImage from "../../assets/whofor.png";

gsap.registerPlugin(ScrollTrigger);

export default function WhoIsItFor() {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const badgeRefs = useRef([]);
  const cardRefs = useRef([]);
  const flowRefs = useRef([]);
  const pathRef = useRef(null);

  const targetUsers = [
    {
      number: "01",
      title: "Small business owners",
      text: "Owners who manage salary, attendance, leave and deductions manually.",
    },
    {
      number: "02",
      title: "Retail shops",
      text: "Teams where half-days, paid leaves and monthly salary changes often.",
    },
    {
      number: "03",
      title: "Clinics & agencies",
      text: "Businesses where employees need salary proof and payslips on time.",
    },
    {
      number: "04",
      title: "Contractors & field teams",
      text: "Staff with changing paid days, leave days, overtime and deductions.",
    },
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".wif-eyebrow, .wif-title, .wif-subtitle",
        { opacity: 0, y: 34 },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 72%",
          },
        }
      );

      gsap.fromTo(
        imageRef.current,
        {
          opacity: 0,
          scale: 0.9,
          rotate: -2,
          clipPath: "inset(12% 12% 12% 12% round 36px)",
        },
        {
          opacity: 1,
          scale: 1,
          rotate: 0,
          clipPath: "inset(0% 0% 0% 0% round 36px)",
          duration: 1.15,
          ease: "power4.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 60%",
          },
        }
      );

      gsap.fromTo(
        badgeRefs.current,
        {
          opacity: 0,
          y: 34,
          scale: 0.86,
          rotate: -6,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: 0,
          duration: 0.75,
          ease: "back.out(1.8)",
          stagger: 0.14,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 50%",
          },
        }
      );

      gsap.fromTo(
        cardRefs.current,
        {
          opacity: 0,
          y: 54,
          scale: 0.94,
          rotateX: 10,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotateX: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 44%",
          },
        }
      );

      gsap.fromTo(
        flowRefs.current,
        {
          opacity: 0,
          y: 28,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.65,
          ease: "power3.out",
          stagger: 0.11,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 36%",
          },
        }
      );

      if (pathRef.current) {
        const length = pathRef.current.getTotalLength();

        gsap.set(pathRef.current, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });

        gsap.to(pathRef.current, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 68%",
            end: "bottom 52%",
            scrub: 1,
          },
        });
      }

      gsap.to(".wif-float-a", {
        y: -14,
        rotate: 2.5,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".wif-float-b", {
        y: 14,
        rotate: -2.5,
        duration: 3.4,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="who-is-it-for" className="wif-section">
      <div className="wif-container">
        <div className="wif-header">
          <span className="wif-eyebrow">Who is it for?</span>

          <h2 className="wif-title">
            For teams where payroll still depends on memory, notes and repeated questions.
          </h2>

          <p className="wif-subtitle">
            SlipSense is made for small businesses that need salary, attendance,
            deductions and payslip proof to stay clear every month.
          </p>
        </div>

        <div className="wif-showcase">
          {/* LEFT STORY IMAGE */}
          <div className="wif-story-panel">
            <div className="wif-image-card" ref={imageRef}>
              <img
                src={whoIsItForImage}
                alt="Business owner struggling with payroll, attendance and payslip proof"
              />
            </div>

            <div
              className="wif-story-badge wif-badge-1 wif-float-a"
              ref={(el) => (badgeRefs.current[0] = el)}
            >
              <span>Owner confusion</span>
              <strong>“Who took leave?”</strong>
            </div>

            <div
              className="wif-story-badge wif-badge-2 wif-float-b"
              ref={(el) => (badgeRefs.current[1] = el)}
            >
              <span>Salary doubt</span>
              <strong>“How much to deduct?”</strong>
            </div>

            <div
              className="wif-story-badge wif-badge-3 wif-float-a"
              ref={(el) => (badgeRefs.current[2] = el)}
            >
              <span>Employee asking</span>
              <strong>“Where is my payslip?”</strong>
            </div>
          </div>

          {/* RIGHT BENTO CONTENT */}
          <div className="wif-bento">
            <div className="wif-pain-card" ref={(el) => (cardRefs.current[0] = el)}>
              <p className="wif-pain-label">Before SlipSense</p>
              <h3>Payroll becomes stressful when attendance and deductions are unclear.</h3>
            </div>

            <div className="wif-audience-grid">
              {targetUsers.map((item, index) => (
                <div
                  className={`wif-user-card wif-user-card-${index + 1}`}
                  key={item.title}
                  ref={(el) => (cardRefs.current[index + 1] = el)}
                >
                  <div className="wif-user-number">{item.number}</div>

                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="wif-flow-card">
              <svg
                className="wif-flow-line"
                viewBox="0 0 720 80"
                preserveAspectRatio="none"
              >
                <path
                  ref={pathRef}
                  d="M20 40 C150 5, 240 75, 360 40 C485 5, 565 75, 700 40"
                  fill="none"
                  stroke="#cbd5e1"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>

              <div className="wif-flow-item" ref={(el) => (flowRefs.current[0] = el)}>
                <span>01</span>
                <strong>Attendance</strong>
              </div>

              <div className="wif-flow-item" ref={(el) => (flowRefs.current[1] = el)}>
                <span>02</span>
                <strong>Deductions</strong>
              </div>

              <div className="wif-flow-item" ref={(el) => (flowRefs.current[2] = el)}>
                <span>03</span>
                <strong>Payroll</strong>
              </div>

              <div className="wif-flow-item" ref={(el) => (flowRefs.current[3] = el)}>
                <span>04</span>
                <strong>Payslip proof</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}